// regeneration-entry-gate.ts — R-141 (2026-10-07, Psion v12 Regenerative Biology): may a BUCKET entry regenerate its holder at the
// start of its turn? Twin of ft_regeneration_bucket_entry_ticks() (check_ability_for_regeneration_and_regenerate_target_arr.php).
// The start-of-turn sweep walks every ability bucket, so the tick refuses what is not an always-on, currently-active passive:
//   · a non-passive definition (a potion / an ability whose regeneration is delivered as a STATUS when used — the status tick owns it);
//   · a trigger-keyed passive (contains_trigger_key: any layer key containing "trigger" — the trigger writes it as a timed status);
//   · a passive the compile stamped inactive (passive_conditions_active false);
//   · a passive whose caster-status conditions fail NOW (check_ability_conditions reads them live: a vitality tier from
//     computed.vitality.v_status, any other id from the held status rows not removed at this turn start).
// Gear and skill gates use the shared condition evaluator; median charge requires an explicit POST-recharge holder. Status/light
// ordering stays turn-local, including statuses removed earlier in this boundary. Unheld contexts remain 'unknown'.
import contract from '../compile/c1-contract.json';
import type { C1Contract } from '../compile/possessor-compile.js';
import { createAbilityConditionEvaluator } from '../compile/ability-conditions.js';
import type { AbilityConditionDependencies } from '../compile/ability-conditions.js';
import { createSkillRequirementChecker } from '../compile/skill-requirements.js';
import { answerConditionContext, ConditionContextUnknown } from '../compile/condition-context.js';
import { createLayerEntriesReader } from '../compile/layer-entries-reader.js';
import { createInventoryReadContext } from '../compile/inventory-read-context.js';
import { readItemField, type ItemFieldRow } from '../compile/item-field-reader.js';
import { createCatalogDefaultProviderReader } from '../compile/provider-catalog-order.js';
import { readNodeItemId } from '../compile/layer-cell-readers.js';
import { findInventoryRow } from '../inventory/flag-adjust.js';
import { VITALITY_STATUS_IDS } from '../reactions/trigger-dispatch.js';
import { statusItemId, statusRows } from '../statuses/expiry.js';

type Row = Record<string, unknown>;
export interface RegenerationGateReaders {
  value: (row: Row | null, key: string) => unknown;
  has: (row: Row | null, key: string) => boolean;
  entries: (row: Row | null, key: string) => unknown[];
  /** R-197: encounter_light_types_present() for the holder's encounter; absent → a light gate is 'unknown'. */
  lightTypesPresent?: (holder: Row, members?: readonly Row[]) => string[] | null;
  /** Full held default-provider definitions; absent/unheld references remain unknown. */
  definition?: (id: string) => Row | null;
  /** The holder with this tick's recharge/decay applied. Relevant charge entries must carry explicit current AND max (including 0).
   *  A pre-tick compile is never evidence of median charge; absent reader/entry/values remain unknown. */
  postRechargeHolder?: (holder: Row) => Row | undefined;
}
export interface RegenerationGateEntry { id: string; inactive: boolean }

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
/** layer_list_entry_uuid(): a bare id, a `id|…` pipe ref, or a {item_id} / {items:{item_id}} shell. */
function entryUuid(entry: unknown): string {
  if (typeof entry === 'string') return upper(entry.split('|')[0]);
  const row = record(entry);
  return upper(scalar(row?.item_id) ?? scalar(record(row?.items)?.item_id) ?? scalar(row?.item_layer_effect) ?? '');
}
/** ability_has_runtime_conditions()'s key set (the host's own layers). */
const isRuntimeCondition = (key: string): boolean => key.startsWith('if_') || key.startsWith('requires_skill_tier_')
  || ['requires_equipped_item', 'draw_sheathed_weapon_for_attack_ability', 'ability_active_if_ability_charge_is_at_median'].includes(key);
const STATUS_GATES = new Set(['if_caster_has_status_effect_active', 'if_caster_has_status_effect_not_active']);
/** R-197 (2026-10-07): the point-based light gates (check_ability_conditions ~L584–647), judged from encounter_light_types_present(). */
const LIGHT_GATES = new Set(['if_light_type_active', 'if_light_type_not_active']);

/** v_status_id_of(): a string id or a {items:{item_id}} shell; null = the tier is not readable. */
function vitalityTier(holder: Row): string | null {
  const vitality = record(record(holder.computed)?.vitality);
  if (!vitality || !('v_status' in vitality)) return null;
  const raw = vitality.v_status;
  if (typeof raw === 'string') return upper(raw);
  const id = scalar(record(record(raw)?.items)?.item_id);
  return typeof id === 'string' ? upper(id) : null;
}
/** PHP empty(): null, false, 0, 0.0, '', '0' and [] are empty. */
export function phpEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === false || value === 0 || value === '' || value === '0'
    || (Array.isArray(value) && value.length === 0);
}
/** passive_ability_entry_is_active() twin, inverted: an entry is inactive only when it carries passive_conditions_active and that value
 *  is PHP-empty (layer_value reads a one-element list as its element) — a string '0' stamp is inactive, as in PHP. */
export function passiveEntryInactive(entry: unknown): boolean {
  const row = record(entry);
  return !!row && 'passive_conditions_active' in row && phpEmpty(scalar(row.passive_conditions_active));
}
/** ItemLayerTypeCatalog::defaultProviderNames(): the carrier slots layer_has() / layer_entries() descend into. */
const DEFAULT_PROVIDERS: ReadonlySet<string> = new Set(Object.entries((contract as { catalogRows: Record<string, { default_provider?: unknown }> }).catalogRows)
  .filter(([, row]) => !phpEmpty(row.default_provider)).map(([name]) => name));
const C = contract as unknown as C1Contract;
const isLateCondition = (key: string): boolean => key === 'requires_equipped_item' || key.startsWith('requires_skill_tier_')
  || key === 'ability_active_if_ability_charge_is_at_median';

/** Discover inherited gates with the same provider walk and readers as the shared evaluator. Flatten only the late AND families;
 *  the turn's existing status/light reader below owns their earlier assign/overwrite semantics (PHP check_ability_conditions:817–855). */
function inheritedConditions(read: RegenerationGateReaders, holder: Row, definition: Row) {
  const missing = (kind: string): never => { throw new ConditionContextUnknown(kind, 'regeneration-context-not-held'); };
  const resolveCarrierRef = (id: string): Row => read.definition?.(upper(id)) ?? missing('provider');
  const deps: AbilityConditionDependencies = { catalogRows: C.catalogRows, abilityTypes: C.abilityTypes,
    aggregationStrategy: (name: string) => C.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
    resolveCarrierRef,
    resolveInventoryInstance: (id: string) => findInventoryRow(holder, id),
    equipmentSlots: C.equipmentSlots, vitalityStatusIds: C.vitalityStatusIds,
    conditionSource: (kind: string, args: readonly unknown[]): unknown => {
      // The shared checker enumerates the ORIGINAL definition's own keys, not new tier keys flattened out of a provider.
      if (kind === 'skills') return createSkillRequirementChecker({ ...deps, skillEntries: C.skillEntries,
        layerEntries: (row, key) => Object.values(readers.read(row, key)) })(definition, holder);
      const scene = { encounter: null, campaign: null, value: read.value, entries: read.entries, abilityTypes: C.abilityTypes,
        // Missing compiled charge fields must not fall back to a pre-recharge definition's default.
        definition: () => missing('median-charge') };
      if (kind === 'equipped-items') return answerConditionContext(kind, args, scene);
      if (kind === 'median-charge') {
        const after = read.postRechargeHolder?.(holder) ?? missing(kind);
        return answerConditionContext(kind, [after, args[1]], scene);
      }
      return missing(kind);
    } };
  const providers = createCatalogDefaultProviderReader({ ...deps, nodeItemId: readNodeItemId });
  // Discovery, listed conditions and skill checks must all read the same resolved provider definitions.
  function* resolvedProviders(node: ItemFieldRow): Generator<ItemFieldRow> {
    for (const raw of providers.providers(node)) {
      const child = raw as Row, id = readNodeItemId(child);
      yield !record(child.item_layer_types) && id ? resolveCarrierRef(id) : child;
    }
  }
  const readers = createLayerEntriesReader({ ...deps, nodeItemId: readNodeItemId, providers: resolvedProviders,
    inventoryContext: createInventoryReadContext(deps.resolveInventoryInstance) });
  const keys = new Set<string>(), seen = new Set<string>();
  function walk(node: Row): void {
    const id = readNodeItemId(node);
    if (id) { if (seen.has(upper(id))) return; seen.add(upper(id)); }
    const own = record(node.item_layer_types) ?? {};
    for (const surface of [node, own]) for (const [key, value] of Object.entries(surface))
      if (isRuntimeCondition(key) && !phpEmpty(value)) keys.add(key);
    for (const child of resolvedProviders(node)) walk(child as Row);
  }
  walk(definition);
  const listed = (key: string): unknown[] => Object.values(readers.read(definition, key));
  const late = Object.fromEntries([...keys].filter(isLateCondition).map(key => [key, listed(key)]));
  return { conditions: [...keys], listed, check: () => createAbilityConditionEvaluator(deps).check({ item_layer_types: late }, holder) };
}

/** Active healing abilities deliver regeneration as a status, never as a held passive. */
export function isPassiveRegenerationDefinition(definition: Row): boolean {
  const type = readItemField(definition, 'item_type');
  return typeof type !== 'string' || type === '' || type === 'passive_ability';
}
export function regenerationEntryTicks(read: RegenerationGateReaders, holder: Row, entry: RegenerationGateEntry, definition: Row,
  removedStatusIds: ReadonlySet<string> = new Set()): boolean | 'unknown' {
  try { return entryTicks(read, holder, entry, definition, removedStatusIds); }
  catch (error) {
    if (error instanceof ConditionContextUnknown) return 'unknown';
    throw error;
  }
}
function entryTicks(read: RegenerationGateReaders, holder: Row, entry: RegenerationGateEntry, definition: Row,
  removedStatusIds: ReadonlySet<string>): boolean | 'unknown' {
  if (!isPassiveRegenerationDefinition(definition)) return false;
  const own = record(definition.item_layer_types) ?? {};
  const keys = [...Object.keys(definition), ...Object.keys(own)];
  if (keys.some(key => key.includes('trigger'))) return false;
  if (entry.inactive) return false;
  // ability_has_runtime_conditions(): own item_layer_types AND the legacy top level, non-empty values only
  const present = (key: string): boolean => !phpEmpty(own[key]) || (key !== 'item_layer_types' && !phpEmpty(definition[key]));
  const direct = [...new Set(keys)].filter(key => isRuntimeCondition(key) && present(key));
  const inherited = Object.keys(own).some(key => DEFAULT_PROVIDERS.has(key)) || record(definition.player_characters)
    || direct.some(isLateCondition) ? inheritedConditions(read, holder, definition) : null;
  const conditions = inherited?.conditions ?? direct;
  if (conditions.some(key => !STATUS_GATES.has(key) && !LIGHT_GATES.has(key) && !isLateCondition(key))) return 'unknown';
  if (conditions.includes('ability_active_if_ability_charge_is_at_median') && !read.postRechargeHolder) return 'unknown';
  if (conditions.some(key => LIGHT_GATES.has(key)) && !read.lightTypesPresent) return 'unknown';
  const held = new Set(statusRows(holder, 'target').filter(s => !removedStatusIds.has(upper(s.status_id))).map(statusItemId));
  const tier = vitalityTier(holder);
  const holds = (id: string): boolean | null => VITALITY_STATUS_IDS.has(id) ? (tier === null ? null : tier === id) : held.has(id);
  const listed = (key: string): string[] => {
    if (inherited) return inherited.listed(key).map(entryUuid).filter(Boolean);
    const raw = phpEmpty(own[key]) ? definition[key] : own[key];
    return (Array.isArray(raw) ? raw : [raw]).map(entryUuid).filter(Boolean);
  };
  // check_ability_conditions(): the _not_active block runs after the _active block and OVERWRITES its verdict (it starts from true)
  let met: boolean | 'unknown' = true;
  if (conditions.includes('if_caster_has_status_effect_active')) {
    const verdicts = listed('if_caster_has_status_effect_active').map(holds);
    met = verdicts.includes(true) ? true : verdicts.includes(null) ? 'unknown' : false;
  }
  if (conditions.includes('if_caster_has_status_effect_not_active')) {
    const verdicts = listed('if_caster_has_status_effect_not_active').map(holds);
    met = verdicts.includes(true) ? false : verdicts.includes(null) ? 'unknown' : true;
  }
  // R-197: the light gates run AFTER the status gates. _active ASSIGNS (any listed type at any point → true, else false — it overwrites
  // the status verdict, as PHP's), _not_active is AND-combined; an unknown base light (null) leaves the verdict untouched (fail-open).
  if (conditions.some(key => LIGHT_GATES.has(key))) {
    const present = read.lightTypesPresent!(holder);
    if (present !== null) {
      const has = new Set(present.map(upper));
      if (conditions.includes('if_light_type_active')) met = listed('if_light_type_active').some(id => has.has(id));
      if (conditions.includes('if_light_type_not_active')) {
        const blocked = listed('if_light_type_not_active').some(id => has.has(id));
        met = met === 'unknown' ? (blocked ? false : 'unknown') : met && !blocked;
      }
    }
  }
  // A false late gate can settle an earlier unknown status; otherwise keep that unknown rather than guessing a vitality tier.
  return inherited && met !== false && !inherited.check() ? false : met;
}
